import { BrainCircuit, Loader2, RefreshCw, Sparkles } from "lucide-react";
import type { ReactNode } from "react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  generateAiInsight,
  getLatestAiInsight,
  type AiInsight,
} from "../../../api/dashboard";
import { ApiClientError } from "../../../api/types";
import { useAuth } from "../../../context/AuthContext";
import { RoleCode } from "../../../lib/rbac";

interface StrategicAiPanelProps {
  startDate: string;
  endDate: string;
  refreshKey?: number;
}

function renderInlineMarkdown(text: string): ReactNode[] {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, index) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return (
        <strong key={index} className="font-semibold text-godo-navy">
          {part.slice(2, -2)}
        </strong>
      );
    }
    return <span key={index}>{part}</span>;
  });
}

function MarkdownInsight({ markdown }: { markdown: string }) {
  const lines = markdown.split(/\r?\n/).filter((line) => line.trim().length > 0);
  const bulletLines = lines.filter((line) => /^[-*•]\s+/.test(line.trim()));
  const paragraphLines = lines.filter((line) => !/^[-*•]\s+/.test(line.trim()));

  return (
    <div className="space-y-3 text-sm leading-relaxed text-slate-700">
      {paragraphLines.length > 0 && (
        <div className="space-y-2">
          {paragraphLines.map((line, index) => (
            <p key={`p-${index}`}>{renderInlineMarkdown(line.trim())}</p>
          ))}
        </div>
      )}

      {bulletLines.length > 0 && (
        <ul className="list-disc space-y-2 ps-5 marker:text-godo-navy">
          {bulletLines.map((line, index) => (
            <li key={`li-${index}`}>
              {renderInlineMarkdown(line.trim().replace(/^[-*•]\s+/, ""))}
            </li>
          ))}
        </ul>
      )}

      {lines.length === 0 && <p className="text-slate-500">—</p>}
    </div>
  );
}

export function StrategicAiPanel({
  startDate,
  endDate,
  refreshKey = 0,
}: StrategicAiPanelProps) {
  const { t } = useTranslation();
  const { hasRole } = useAuth();
  const canGenerate = hasRole(RoleCode.SUPER_ADMIN, RoleCode.FINANCIAL_ADVISOR);

  const [insight, setInsight] = useState<AiInsight | null>(null);
  const [isLoadingInsight, setIsLoadingInsight] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadLatestInsight = useCallback(async () => {
    setIsLoadingInsight(true);
    setError(null);
    try {
      const latest = await getLatestAiInsight();
      setInsight(latest);
    } catch (err) {
      setError(
        err instanceof ApiClientError
          ? err.message
          : err instanceof Error
            ? err.message
            : t("dashboard.ai_load_failed"),
      );
    } finally {
      setIsLoadingInsight(false);
    }
  }, [t]);

  useEffect(() => {
    void loadLatestInsight();
  }, [loadLatestInsight, refreshKey]);

  async function handleGenerateInsight() {
    if (!canGenerate) {
      setError(t("dashboard.ai_write_forbidden"));
      return;
    }

    setIsGenerating(true);
    setError(null);

    try {
      const generated = await generateAiInsight(startDate, endDate);
      setInsight({
        insightId: generated.insightId,
        orgId: generated.orgId,
        generatedAt: generated.generatedAt,
        contextSnapshotJson: JSON.stringify(generated.contextSnapshot),
        aiResponseMarkdown: generated.aiResponseMarkdown,
        isActioned: 0,
      });
    } catch (err) {
      setError(
        err instanceof ApiClientError
          ? err.message
          : err instanceof Error
            ? err.message
            : t("dashboard.ai_generate_failed"),
      );
    } finally {
      setIsGenerating(false);
    }
  }

  return (
    <article className="overflow-hidden rounded-2xl border border-sky-200/80 bg-gradient-to-br from-sky-50 via-white to-slate-50 shadow-lg ring-1 ring-godo-navy/5">
      <div className="border-b border-sky-100 bg-gradient-to-r from-godo-navy/95 to-[#00408f]/90 px-5 py-4 sm:px-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/10 text-sky-100 ring-1 ring-white/20">
              <BrainCircuit className="h-5 w-5" aria-hidden="true" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-sky-200" aria-hidden="true" />
                <h2 className="text-lg font-semibold tracking-tight text-white">
                  {t("dashboard.strategic_insight")}
                </h2>
              </div>
              <p className="mt-1 text-xs text-sky-100/85">
                {t("dashboard.ai_subtitle")}
              </p>
            </div>
          </div>

          {insight?.generatedAt && (
            <p className="text-xs text-sky-100/90">
              {t("dashboard.ai_generated_at")}:{" "}
              <span dir="ltr" className="font-mono font-medium text-white">
                {insight.generatedAt}
              </span>
            </p>
          )}
        </div>
      </div>

      <div className="space-y-4 px-5 py-5 sm:px-6">
        {error && (
          <div
            role="alert"
            className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
          >
            {error}
          </div>
        )}

        <div className="rounded-xl border border-sky-100 bg-white/90 p-5 shadow-inner">
          {isLoadingInsight && (
            <p className="text-sm text-slate-500">{t("labels.loading")}</p>
          )}

          {!isLoadingInsight && !insight && (
            <p className="text-sm text-slate-500">{t("dashboard.ai_empty")}</p>
          )}

          {!isLoadingInsight && insight && (
            <MarkdownInsight markdown={insight.aiResponseMarkdown} />
          )}
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => void handleGenerateInsight()}
            disabled={!canGenerate || isGenerating}
            className="inline-flex items-center gap-2 rounded-lg bg-godo-navy px-4 py-2.5 text-sm font-semibold text-white shadow-md transition hover:bg-[#003d8f] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isGenerating ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                {t("dashboard.ai_generating")}
              </>
            ) : (
              <>
                <BrainCircuit className="h-4 w-4" aria-hidden="true" />
                {t("dashboard.ai_ask_button")}
              </>
            )}
          </button>

          <button
            type="button"
            onClick={() => void loadLatestInsight()}
            disabled={isGenerating}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <RefreshCw className="h-4 w-4" aria-hidden="true" />
            {t("labels.refresh")}
          </button>

          {!canGenerate && (
            <p className="text-xs text-slate-500">{t("dashboard.ai_write_forbidden")}</p>
          )}
        </div>
      </div>
    </article>
  );
}
